/**
 * Politique de confidentialité — RGPD-compliant (Articles 13-14).
 *
 * Covers: data controller, purposes, legal basis, retention, rights, contact.
 */
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

import PublicFooter from "../components/layout/PublicFooter";
import { goBack } from "../lib/navigation";


export default function Confidentialite() {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen flex flex-col bg-shariti-cream">
            <main className="flex-1 max-w-3xl mx-auto px-6 py-10 md:py-14 w-full">
                <button
                    onClick={() => goBack(navigate, "/login")}
                    data-testid="back-to-login"
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-shariti-orange transition"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Retour
                </button>

                <h1 className="mt-6 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                    Politique de confidentialité
                </h1>
                <p className="mt-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                    Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                </p>

                <div className="mt-8 space-y-7 text-sm leading-relaxed text-slate-700">
                    <div className="rounded-2xl bg-shariti-orange-soft border border-orange-200 p-4 text-[13px] text-shariti-black font-medium">
                        <strong>En résumé :</strong> PREREC est utilisée par <strong>Shariti</strong> pour gérer
                        les candidatures reçues, qualifier les profils et organiser les entretiens. Vos données
                        sont conservées <strong>2 ans maximum</strong> après le dernier contact. Vous pouvez à
                        tout moment exercer vos droits d'accès, de rectification, de suppression et d'opposition
                        en écrivant à{" "}
                        <a className="text-shariti-orange hover:underline font-bold" href="mailto:smile@shariti.com">
                            smile@shariti.com
                        </a>.
                    </div>

                    <Section title="1. Responsable du traitement">
                        <p>
                            Le responsable du traitement des données personnelles est <strong>SHARITI</strong>,
                            joignable à l'adresse{" "}
                            <a className="text-shariti-orange hover:underline" href="mailto:smile@shariti.com">
                                smile@shariti.com
                            </a>.
                        </p>
                    </Section>

                    <Section title="2. Données collectées">
                        <p>Dans le cadre du traitement des candidatures, PREREC collecte et stocke les données suivantes :</p>
                        <ul className="list-disc ml-5 mt-2 space-y-1">
                            <li>Nom et prénom</li>
                            <li>Adresse email</li>
                            <li>Numéro de téléphone</li>
                            <li>Curriculum vitae (CV) et lettres de motivation</li>
                            <li>Données issues du formulaire de candidature (ville, disponibilités, mobilité, message, etc.)</li>
                            <li>Scoring interne (score IA calculé à partir des informations de candidature)</li>
                            <li>Commentaires internes de recrutement saisis par l'équipe Shariti</li>
                            <li>Historique de suivi candidat (statuts, tentatives d'appel, rendez-vous, recommandations)</li>
                        </ul>
                    </Section>

                    <Section title="3. Finalités du traitement">
                        <p>Les données sont collectées et traitées pour les finalités suivantes :</p>
                        <ul className="list-disc ml-5 mt-2 space-y-1">
                            <li>Gestion des candidatures reçues via les différentes sources d'acquisition utilisées par Shariti (site internet, outils de gestion de recrutement, sites d'annonces partenaires, formulaires en ligne)</li>
                            <li>Qualification des profils par l'équipe pré-recrutement</li>
                            <li>Organisation et suivi des entretiens immersifs</li>
                            <li>Constitution d'un vivier candidats pour des opportunités futures</li>
                            <li>Suivi global du processus de recrutement</li>
                        </ul>
                    </Section>

                    <Section title="4. Base légale">
                        <p>
                            Le traitement des données candidats est fondé sur l'<strong>intérêt légitime</strong> de
                            Shariti à gérer ses processus de recrutement, et sur le <strong>consentement explicite</strong>{" "}
                            du candidat lorsqu'il soumet sa candidature via nos canaux d'acquisition
                            (article 6.1.a et 6.1.f du RGPD).
                        </p>
                    </Section>

                    <Section title="5. Destinataires des données">
                        <p>Les données candidats sont accessibles uniquement aux personnes habilitées par Shariti :</p>
                        <ul className="list-disc ml-5 mt-2 space-y-1">
                            <li>Administrateurs Back-Office RH</li>
                            <li>Pré-recruteurs en charge des appels de qualification</li>
                            <li>Référents terrain en charge des entretiens immersifs</li>
                        </ul>
                        <p className="mt-2">
                            <strong>Aucune donnée n'est cédée, louée ou vendue à des tiers</strong>. Les sous-traitants
                            techniques (hébergement, fournisseurs de services IA pour le scoring) sont liés contractuellement
                            par des obligations de confidentialité et de conformité RGPD.
                        </p>
                    </Section>

                    <Section title="6. Durée de conservation">
                        <p>
                            Les données candidats sont conservées pendant une durée maximale de{" "}
                            <strong>2 ans à compter du dernier contact</strong> avec le candidat (dernier appel,
                            dernier rendez-vous, dernier échange écrit).
                        </p>
                        <p>
                            Au-delà de ce délai, les données sont automatiquement archivées puis supprimées, sauf
                            obligation légale de conservation ou consentement explicite du candidat à un maintien
                            prolongé dans le vivier.
                        </p>
                    </Section>

                    <Section title="7. Sécurité">
                        <p>
                            PREREC met en œuvre des mesures techniques et organisationnelles pour garantir la sécurité
                            des données : authentification individuelle, contrôle d'accès par rôle, chiffrement des
                            communications (TLS), chiffrement au repos de la base de données, journalisation des accès
                            administrateurs et sauvegardes quotidiennes.
                        </p>
                    </Section>

                    <Section title="8. Vos droits">
                        <p>
                            Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi
                            Informatique et Libertés, vous disposez à tout moment des droits suivants concernant
                            vos données personnelles :
                        </p>
                        <ul className="list-disc ml-5 mt-2 space-y-1">
                            <li><strong>Droit d'accès</strong> — obtenir une copie des données vous concernant</li>
                            <li><strong>Droit de rectification</strong> — corriger une donnée inexacte ou incomplète</li>
                            <li><strong>Droit de suppression</strong> (droit à l'oubli) — demander l'effacement de vos données</li>
                            <li><strong>Droit d'opposition</strong> — vous opposer au traitement de vos données</li>
                            <li><strong>Droit à la limitation</strong> — limiter le traitement dans certains cas</li>
                            <li><strong>Droit à la portabilité</strong> — récupérer vos données dans un format structuré</li>
                        </ul>
                        <p className="mt-2">
                            Pour exercer l'un de ces droits, écrivez à{" "}
                            <a className="text-shariti-orange hover:underline font-bold" href="mailto:smile@shariti.com">
                                smile@shariti.com
                            </a>{" "}
                            en précisant votre demande et en joignant un justificatif d'identité. Nous nous engageons
                            à vous répondre dans un délai maximal d'<strong>un mois</strong>.
                        </p>
                    </Section>

                    <Section title="9. Réclamation auprès de la CNIL">
                        <p>
                            Si vous estimez, après nous avoir contactés, que vos droits Informatique et Libertés
                            ne sont pas respectés, vous pouvez adresser une réclamation à la{" "}
                            <a className="text-shariti-orange hover:underline" href="https://www.cnil.fr" target="_blank" rel="noreferrer noopener">
                                Commission Nationale de l'Informatique et des Libertés (CNIL)
                            </a>.
                        </p>
                    </Section>

                    <Section title="10. Cookies et traceurs">
                        <p>
                            PREREC est une plateforme interne d'authentification : elle n'utilise <strong>aucun cookie
                            publicitaire ni traceur tiers</strong>. Seuls les cookies strictement nécessaires au
                            fonctionnement (jetons d'authentification, préférences de session) sont déposés.
                        </p>
                    </Section>

                    <Section title="11. Modifications">
                        <p>
                            La présente politique peut être mise à jour pour refléter les évolutions légales ou
                            techniques. La date de dernière mise à jour figure en haut de cette page.
                        </p>
                    </Section>
                </div>
            </main>
            <PublicFooter />
        </div>
    );
}


function Section({ title, children }) {
    return (
        <section>
            <h2 className="text-lg font-extrabold text-shariti-black mb-2">{title}</h2>
            <div className="space-y-2">{children}</div>
        </section>
    );
}
